import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Download, FileEdit, CheckCircle2, LayoutTemplate } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { reports, Report } from '../lib/data';
function StatusBadge({
  status
}: {
  status: Report['status'];
}) {
  if (status === 'draft') {
    return <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200" data-id="element-1343">
        <FileEdit className="h-3 w-3" data-id="element-1344" />
        <span className="font-medium" data-id="element-1345">Bozza</span>
      </div>;
  }
  if (status === 'completed') {
    return <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200" data-id="element-1346">
        <CheckCircle2 className="h-3 w-3" data-id="element-1347" />
        <span className="font-medium" data-id="element-1348">Completato</span>
      </div>;
  }
  return null;
}
const templateLabels: Record<string, string> = {
  executive: 'Executive Summary',
  standard: 'Report Standard',
  full: 'Analisi Completa'
};
export function ReportGenerator() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'draft' | 'completed'>('all');
  const filtered = reports.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full" data-id="element-1349">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between mb-10 md:mb-12 gap-4" data-id="element-1350">
        <div data-id="element-1351">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1352">
            Report
          </h1>
        </div>
        <Button onClick={() => navigate('/reports/new')} className="rounded-full h-11 px-6 text-sm" data-id="element-1353">
          <Plus className="h-4 w-4 mr-1.5" data-id="element-1354" />
          Nuovo report
        </Button>
      </header>

      {/* Filter tabs */}
      <div className="bg-card border border-border rounded-lg" data-id="element-1355">
        <div className="flex items-center gap-6 px-6 pt-5 pb-0 text-sm" data-id="element-1356">
          {(['all', 'draft', 'completed'] as const).map(f => {
          const count = f === 'all' ? reports.length : reports.filter(r => r.status === f).length;
          const label = f === 'all' ? 'Tutti' : f === 'draft' ? 'Bozze' : 'Completati';
          const active = filter === f;
          return <button key={f} onClick={() => setFilter(f)} className={`pb-3 border-b-2 transition-colors ${active ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1357">
                {label}{' '}
                <span className="text-muted-foreground ml-1" data-id="element-1358">({count})</span>
              </button>;
        })}
        </div>

        <Separator data-id="element-1359" />

        {/* Reports list */}
        {filtered.length === 0 ? <div className="text-center py-16" data-id="element-1360">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" data-id="element-1361" />
            <p className="text-sm text-muted-foreground" data-id="element-1362">
              Nessun report in questo filtro
            </p>
          </div> : <div className="divide-y divide-border" data-id="element-1363">
            {filtered.map(r => <div key={r.id} className="w-full text-left py-5 md:py-6 px-6 hover:bg-muted/30 transition-colors flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-1364">
                {/* Left: info */}
                <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-1365">
                  <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1366">
                    <LayoutTemplate className="h-4 w-4 text-muted-foreground" data-id="element-1367" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1368">
                    <div className="flex items-center gap-3 mb-1" data-id="element-1369">
                      <h3 className="font-[400] truncate text-[14px]" data-id="element-1370">
                        {r.name}
                      </h3>
                      <StatusBadge status={r.status} data-id="element-1371" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground font-normal" data-id="element-1372">
                      <span className="text-[12px]" data-id="element-1373">
                        {templateLabels[r.template]}
                      </span>
                      <span data-id="element-1374">·</span>
                      <span className="text-[12px]" data-id="element-1375">{r.dateRange}</span>
                      <span data-id="element-1376">·</span>
                      <span className="text-[12px]" data-id="element-1377">
                        Creato il{' '}
                        {new Date(r.createdAt).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: metrics & actions */}
                <div className="flex items-center justify-between md:justify-end gap-8 md:gap-12 flex-shrink-0 pl-14 md:pl-0 w-full md:w-auto" data-id="element-1378">
                  <div className="text-left md:text-right" data-id="element-1379">
                    <div className="text-xl md:text-2xl font-[400] leading-none" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1380">
                      {r.articleCount}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1" data-id="element-1381">
                      Articoli
                    </div>
                  </div>

                  <div className="flex items-center gap-2" data-id="element-1382">
                    {r.status === 'completed' ? <Button variant="outline" size="sm" className="rounded-full h-9" data-id="element-1383">
                        <Download className="h-3.5 w-3.5 mr-1.5" data-id="element-1384" />
                        Scarica PDF
                      </Button> : <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => navigate('/reports/new')} data-id="element-1385">
                        <FileEdit className="h-3.5 w-3.5 mr-1.5" data-id="element-1386" />
                        Modifica
                      </Button>}
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}